import { mapDeviceState } from '@/app-shell/chronicle/state/map-device-state';
import type { ChronicleConnectionContext, ChronicleDeviceState } from '@/atoms/chronicle';
import { CONNECTION_STATUS } from '@/atoms/chronicle';
import type { DeviceState } from '@/tauri/chronicle/types';

const AUTO_RECONNECT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export interface StateEventContext {
  prevStatus: string | null;
  lastDisconnectReason: ChronicleConnectionContext['lastDisconnectReason'];
  linkedDeviceId: string | null;
}

export interface StateEventResult {
  newDeviceState: ChronicleDeviceState;
  newStatus: string;
  isUnexpectedDisconnect: boolean;
  /** Deliberate low-battery shutdown — distinct from a link failure. */
  isPoweroffBatlow: boolean;
  isSuccessfulConnection: boolean;
  bleDeviceId: string | null;
  firmwareVersion: string | null | undefined;
  connectionContextUpdate: Partial<ChronicleConnectionContext> | null;
}

/**
 * Determines if the device is now in a connected state based on event type.
 */
function isConnectedState(eventType: DeviceState['type']): boolean {
  return (
    eventType === 'connected' || eventType === 'recording' || eventType === 'syncing'
  );
}

/**
 * Process a Chronicle state change event and determine the resulting state updates.
 * This is a pure function that can be easily tested.
 */
export function processStateEvent(
  event: DeviceState,
  prevDeviceState: ChronicleDeviceState,
  context: StateEventContext
): StateEventResult {
  const { prevStatus, lastDisconnectReason, linkedDeviceId } = context;
  const eventType = event.type;

  const newDeviceState = mapDeviceState(event, prevDeviceState);
  const newStatus = newDeviceState.connectionStatus;

  const isNowDisconnected = eventType === 'disconnected';
  const isNowConnecting = eventType === 'connecting';
  const isNowReconnecting = eventType === 'reconnecting';
  const isNowConnected = isConnectedState(eventType);
  const wasConnected = prevStatus === CONNECTION_STATUS.CONNECTED;

  // Check if this is a manual disconnect (forgetDevice sets this before calling bridge)
  const isManualDisconnect = lastDisconnectReason === 'manual';
  // Deduped on the reason, not on `wasConnected`: Rust can arm a reconnect
  // before the power-off lands, so the event legitimately arrives from
  // 'reconnecting'. Re-sends while already parked carry the reason forward.
  const isPoweroffBatlow =
    eventType === 'disconnected' &&
    event.reason === 'poweroff_batlow' &&
    lastDisconnectReason !== 'poweroff_batlow';

  // Unexpected disconnect is detected when we go from 'connected' to 'connecting'/'reconnecting'
  // (Rust triggers auto-reconnect immediately on transport end) or directly to 'disconnected'
  // BUT only if it wasn't a manual disconnect/forget action or a deliberate batlow power-off.
  const isUnexpectedDisconnect =
    wasConnected &&
    (isNowConnecting || isNowReconnecting || isNowDisconnected) &&
    !isManualDisconnect &&
    !isPoweroffBatlow;

  const bleDeviceId = 'deviceId' in event ? event.deviceId : null;
  const firmwareVersion = 'firmwareVersion' in event ? event.firmwareVersion : undefined;

  let connectionContextUpdate: Partial<ChronicleConnectionContext> | null = null;

  if (isPoweroffBatlow && linkedDeviceId) {
    // Deliberate device shutdown — no auto-reconnect window / escalation.
    connectionContextUpdate = {
      lastDisconnectReason: 'poweroff_batlow',
      autoReconnectUntil: null,
      autoReconnectAttempts: 0,
    };
  } else if (isUnexpectedDisconnect && linkedDeviceId) {
    connectionContextUpdate = {
      lastDisconnectReason: 'unexpected',
      autoReconnectUntil: Date.now() + AUTO_RECONNECT_TIMEOUT_MS,
      autoReconnectAttempts: 0,
      // Keep linkedDeviceId for reconnection
    };
  } else if (isNowConnected && bleDeviceId) {
    // Only reset reconnect state — linkedDeviceId is managed exclusively by
    // pairing hooks (MongoDB _id from POST) and useRestoreLinkedDevice (GET list).
    connectionContextUpdate = {
      lastDisconnectReason: null,
      autoReconnectUntil: null,
      autoReconnectAttempts: 0,
    };
  }

  // APP-4361: Only treat as a new connection when transitioning FROM a
  // non-connected state. Without this guard every incremental state update
  // (battery, firmware, storage) while already connected fires duplicate
  // trackDeviceConnected events.
  const wasAlreadyConnected = prevStatus === CONNECTION_STATUS.CONNECTED;

  return {
    newDeviceState,
    newStatus,
    isUnexpectedDisconnect,
    isPoweroffBatlow,
    isSuccessfulConnection: isNowConnected && !!bleDeviceId && !wasAlreadyConnected,
    bleDeviceId,
    firmwareVersion,
    connectionContextUpdate,
  };
}
